import { Grid3x3, Headphones, Mic, MicOff, Pause, Play, UserPlus } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { Pop, PopHost } from "../shell/Pop";
import { AudioPicker } from "./audio/AudioPicker";
import type { CallAudio } from "./audio/apply";
import type { CallState } from "./CallProvider";

/**
 * Why a third party cannot be invited, in the operator's words.
 *
 * The server decides WHICH of these applies - it rests on the room's sid and the
 * customer's leg, neither of which the window is told about - and this only
 * names them.
 *
 * ⚠ EVERY CODE THE SERVER CAN SEND NEEDS AN ENTRY. A missing one does not fail
 * loudly: the lookup yields "", which reads as "nothing is in the way", and the
 * control would be offered and then refused by the server with no explanation
 * anywhere. `not_live` is here for exactly that reason - the browser's own
 * "active" and the row's `in-progress` can disagree for a tick.
 */
const TRANSFER_BLOCKED: Record<string, string> = {
  not_conference: "calls.transfer.blockedLegacy",
  not_bridged: "calls.transfer.blockedNotBridged",
  not_live: "calls.transfer.errNotLive",
  in_progress: "calls.transfer.blockedInProgress",
};

/**
 * The row of controls under a live call: mute, hold, keypad, transfer, audio.
 *
 * Lifted out of `LiveCall.tsx`. Its own file because it is the surface that
 * grows: every control an operator reaches for mid-call lands here, and each one
 * arrives with a disabled state, a reason for being disabled, and a label -
 * which is three lines of judgement per button on a file that was already the
 * longest in the telephony tree.
 *
 * FIVE CELLS AND NOT ONE MORE. The row is 470px wide less the call column's own
 * padding, which is 406px, and five 68px cells with 10px gaps are 380px: a sixth
 * wraps onto a line of its own. It had one - a "Recording" marker that was not a
 * button at all and repeated, in a shape that looks pressable, the line the
 * clock above already carries. It is gone; the status stays where it was.
 *
 * Nothing here decides anything. Every button reports a click and reads its
 * state off the row; the decisions live in `CallProvider`.
 */
export function CallControls({
  state,
  padOpen,
  transferOpen,
  getAudio,
  onMute,
  onHold,
  onTogglePad,
  onToggleTransfer,
}: {
  state: CallState;
  /** The keypad is showing, so its cell is lit. */
  padOpen: boolean;
  /** The transfer field is showing. The two share a slot and only one opens. */
  transferOpen: boolean;
  /** Twilio's routing, so a device changed mid-call takes effect mid-call. */
  getAudio: () => CallAudio | null;
  onMute: () => void;
  onHold: () => void;
  onTogglePad: () => void;
  onToggleTransfer: () => void;
}) {
  const t = useT();

  // Why hold cannot be offered, or "" when it can. One key per reason, the way
  // the dial pad's own blocked states do it - a disabled button with no
  // explanation is the same as a broken one.
  // A taken-over AI call first: it is not a conference bridge, but "placed
  // before the conference bridge was switched on" would be untrue of it.
  const holdBlocked = state.takeover
    ? "calls.dial.takeoverNoHold"
    : state.conference
      ? ""
      : state.direction === "inbound"
        ? "calls.dial.holdUnavailableInbound"
        : "calls.dial.holdUnavailableLegacy";

  // Same rule for the transfer cell, with one difference: an extension already
  // in progress is a reason to OPEN the panel rather than to refuse it, because
  // the panel is where the third party is shown and taken off again.
  const transferLive = state.transfer !== null;
  const transferBlocked = state.takeover
    ? "calls.dial.takeoverNoHold"
    : transferLive
      ? ""
      : TRANSFER_BLOCKED[state.transferBlocked] ?? "";

  // THREE CELLS GO DEAD ON A MOBILE CALL, and they are exactly the three that
  // operate on the Voice SDK's `Call` object: mute is `call.mute()`, the keypad
  // is `call.sendDigits()`, and the audio panel routes the SDK's own streams.
  // On a call taken on the operator's telephone there is no such object - the
  // browser is a window onto the call, not a party to it - so every one of them
  // would be a button that silently does nothing.
  //
  // Hold and transfer are deliberately NOT on this list. Both are REST
  // operations on a conference participant, performed by our server, and they
  // work exactly as they do in the browser - which is the whole reason this
  // feature could be built on the conference bridge at all.
  const noBrowserLeg = state.viaMobile ? "calls.dial.mobileNoBrowserLeg" : "";

  return (
    // `flex-wrap` so a future sixth cell degrades onto a second line instead of
    // squashing: the cells hold a fixed-width disc, so an overflowing row shrinks
    // the labels out from under it. Five is what fits today - see the note above
    // the component for the arithmetic.
    <div className="mt-[22px] flex flex-wrap justify-center gap-2.5">
      <button
        type="button"
        className={cn("crm-lc", state.muted && "is-on")}
        onClick={onMute}
        disabled={!!noBrowserLeg}
        title={noBrowserLeg ? t(noBrowserLeg) : undefined}
      >
        <span className="crm-lc-b">
          {state.muted ? <MicOff className="size-4" /> : <Mic className="size-4" />}
        </span>
        <span>{t(state.muted ? "calls.dial.unmute" : "calls.dial.mute")}</span>
      </button>
      {/* Between mute and the keypad, so the two most-used controls stay
          leftmost and nothing that was already there moves. */}
      <button
        type="button"
        className={cn("crm-lc", state.hold && "is-hold")}
        onClick={onHold}
        disabled={!state.conference || state.holdPending}
        title={holdBlocked ? t(holdBlocked) : undefined}
      >
        <span className="crm-lc-b">
          {state.hold ? <Play className="size-4" /> : <Pause className="size-4" />}
        </span>
        <span>
          {state.holdPending
            ? t("calls.dial.holdBusy")
            : t(state.hold ? "calls.dial.resume" : "calls.dial.hold")}
        </span>
      </button>
      <button
        type="button"
        className={cn("crm-lc", padOpen && "is-on")}
        onClick={onTogglePad}
        disabled={!!noBrowserLeg}
        title={noBrowserLeg ? t(noBrowserLeg) : undefined}
      >
        <span className="crm-lc-b">
          <Grid3x3 className="size-4" />
        </span>
        <span>{t("calls.dial.keypad")}</span>
      </button>
      {/* Last of the four, and after the keypad rather than beside hold: it is
          the only one that opens a field to type a stranger's number into, and
          the row is ordered by how often a hand reaches for it. */}
      <button
        type="button"
        className={cn("crm-lc", (transferOpen || transferLive) && "is-on")}
        onClick={onToggleTransfer}
        disabled={!!transferBlocked}
        title={transferBlocked ? t(transferBlocked) : undefined}
      >
        <span className="crm-lc-b">
          <UserPlus className="size-4" />
        </span>
        <span>{t("calls.transfer.label")}</span>
      </button>
      <AudioCell getAudio={getAudio} blocked={noBrowserLeg} />
    </div>
  );
}

/**
 * The fifth cell, and the one reached for under the most pressure: it is pressed
 * while somebody is on the line saying they cannot hear anything. A browser that
 * cannot switch devices still has a microphone worth metering, and the panel
 * says so itself rather than making the operator guess at a dead cell.
 *
 * ONE EXCEPTION, and it is not a browser limitation: a call taken on the
 * operator's own mobile does not pass its audio through this browser at all, so
 * there is nothing here to meter or route. See `blocked`.
 *
 * A DROPDOWN, not the shared panel slot the keypad and the transfer field use.
 * Those two are a keypad and one field; this is three device lists, and opened
 * inside the call column it made the window twice as tall as the screen - the
 * red button below a scroll, and the AI panel beside it left standing in a
 * column half of which was empty. A dropdown is also the shape the dial pad's
 * own audio control already has, so the same panel is now reached the same way
 * in both places.
 *
 * Its own component because of the `PopHost`. A `Pop` reads its open slot from
 * one, a `Pop` without one is a button that silently does nothing, and nothing
 * above the call window has one: `CallProvider` hangs off the layout root rather
 * than off a section, and the sections are where this app mounts them. The pad
 * solves it the same way one file over (`Dialer.tsx`). Scoped to this cell
 * rather than to the row, since this is the only menu on it - and the host
 * renders no DOM, so the row is unchanged.
 */
function AudioCell({
  getAudio,
  blocked,
}: {
  getAudio: () => CallAudio | null;
  /**
   * The one situation in which this cell IS disabled, against the rule above:
   * the audio of a call taken on a telephone does not pass through this browser
   * at all, so there is no microphone to meter and no speaker to switch. The
   * panel would open on three empty lists.
   */
  blocked: string;
}) {
  const t = useT();
  return (
    <PopHost>
      <Pop
        className="crm-lc-pop"
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-lc", open && "is-on")}
            aria-haspopup="dialog"
            aria-expanded={open}
            disabled={!!blocked}
            title={blocked ? t(blocked) : undefined}
          >
            <span className="crm-lc-b">
              <Headphones className="size-4" />
            </span>
            <span>{t("calls.audio.label")}</span>
          </button>
        )}
      >
        <AudioPicker liveAudio={getAudio()} />
      </Pop>
    </PopHost>
  );
}
